function pickField(kv, aliases, { excludeKeyPattern } = {}) {
  if (!kv || typeof kv !== 'object') return '';
  for (const key of aliases) {
    const exact = kv[key];
    if (exact != null && String(exact).trim() !== '') return String(exact).trim();
  }
  for (const [k, v] of Object.entries(kv)) {
    if (v == null) continue;
    const keyText = String(k);
    if (excludeKeyPattern && excludeKeyPattern.test(keyText)) continue;
    if (aliases.some((alias) => keyText.includes(alias))) {
      const text = String(v).trim();
      if (text) return text;
    }
  }
  return '';
}

const SELLER_META_KEY = /(?:地址|电话|开户|账号|银行|行$)/;
const BUYER_KEY = /购|买|旅客|乘客|Passenger|Buyer|buyer/i;
const TRAVEL_ITINERARY_HINT = /行程单|电子客票|机票|航空运输|客票|航班|Itinerary|ETicket/i;

function collectKvText(kv) {
  return Object.entries(kv || {}).map(([k, v]) => `${k}|${v}`).join('\n');
}

function isTravelItineraryLike(kv) {
  return TRAVEL_ITINERARY_HINT.test(collectKvText(kv));
}

function hasBuyerIdentityFields(kv) {
  return Boolean(pickBuyerName(kv) || pickBuyerTaxNo(kv));
}

function hasSellerSideFields(kv) {
  return Object.keys(kv || {}).some((key) => {
    const keyText = String(key);
    if (BUYER_KEY.test(keyText)) return false;
    return /销售方|销方|Seller|seller/i.test(keyText);
  });
}

const BUYER_BLOCK_KEY = /^购买方(?:信息|单位)?$|^购方(?:信息|单位)?$|^购买方$|^购方$/;
const SELLER_BLOCK_KEY = /^销售方(?:信息|单位)?$|^销方(?:信息|单位)?$|^销售方$|^销方$/;
const PARTY_SECTION_LABEL = /^(?:购买|销售|购|销)方(?:信息|单位)?$/;

function isPartySectionLabel(text) {
  const value = String(text || '').trim();
  return Boolean(value) && PARTY_SECTION_LABEL.test(value);
}

function looksLikeTaxNo(text) {
  const normalized = String(text || '')
    .trim()
    .replace(/\s+/g, '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
  return normalized.length >= 15 && normalized.length <= 20;
}

function looksLikeCompanyName(text) {
  const value = String(text || '').trim();
  if (!value || value.length < 4) return false;
  if (isPartySectionLabel(value)) return false;
  if (looksLikeTaxNo(value)) return false;
  if (/^(名称|统一社会信用代码|纳税人识别号)/.test(value)) return false;
  return true;
}

function hasDegradedPartyInfoBlocks(kv) {
  return Object.entries(kv || {}).some(([key, value]) => {
    const keyText = String(key).trim();
    if (!BUYER_BLOCK_KEY.test(keyText) && !SELLER_BLOCK_KEY.test(keyText)) return false;
    return isPartySectionLabel(value);
  });
}

function parsePartyBlockFields(value) {
  const text = String(value || '').trim();
  if (!text) return { name: '', taxNo: '' };

  const nameMatch = text.match(/名称\s*[:：]\s*([^\n\r]+)/);
  const taxMatch = text.match(
    /(?:统一社会信用代码(?:\/纳税人识别号)?|纳税人识别号)\s*[:：]\s*([0-9A-Z]+)/i
  );

  if (nameMatch) {
    return {
      name: nameMatch[1].trim(),
      taxNo: taxMatch ? taxMatch[1].trim() : ''
    };
  }

  const firstLine = text.split(/[\n\r]/)[0].trim();
  if (looksLikeCompanyName(firstLine)) {
    return {
      name: firstLine,
      taxNo: taxMatch ? taxMatch[1].trim() : ''
    };
  }

  return {
    name: '',
    taxNo: taxMatch ? taxMatch[1].trim() : ''
  };
}

function hasPartyInfoBlockKeys(kv) {
  return Object.keys(kv || {}).some((key) => {
    const keyText = String(key).trim();
    return BUYER_BLOCK_KEY.test(keyText) || SELLER_BLOCK_KEY.test(keyText);
  });
}

function pickPartyBlockName(kv, side) {
  const blockPattern = side === 'buyer' ? BUYER_BLOCK_KEY : SELLER_BLOCK_KEY;
  const exactKeys = side === 'buyer'
    ? ['购买方信息', '购方信息', '购买方', '购方']
    : ['销售方信息', '销方信息', '销售方', '销方'];

  for (const key of exactKeys) {
    const val = kv?.[key];
    if (val != null && String(val).trim()) {
      const { name } = parsePartyBlockFields(val);
      if (name) return name;
    }
  }

  for (const [k, v] of Object.entries(kv || {})) {
    if (v == null) continue;
    if (blockPattern.test(String(k).trim())) {
      const { name } = parsePartyBlockFields(v);
      if (name) return name;
    }
  }
  return '';
}

function pickPartyBlockTaxNo(kv, side) {
  const blockPattern = side === 'buyer' ? BUYER_BLOCK_KEY : SELLER_BLOCK_KEY;
  const exactKeys = side === 'buyer'
    ? ['购买方信息', '购方信息', '购买方', '购方']
    : ['销售方信息', '销方信息', '销售方', '销方'];

  for (const key of exactKeys) {
    const val = kv?.[key];
    if (val != null && String(val).trim()) {
      const { taxNo } = parsePartyBlockFields(val);
      if (taxNo) return taxNo;
    }
  }

  for (const [k, v] of Object.entries(kv || {})) {
    if (v == null) continue;
    if (blockPattern.test(String(k).trim())) {
      const { taxNo } = parsePartyBlockFields(v);
      if (taxNo) return taxNo;
    }
  }
  return '';
}

function shouldUseGenericSellerFields(kv) {
  if (isTravelItineraryLike(kv)) return false;
  if (hasPartyInfoBlockKeys(kv)) return false;
  if (hasBuyerIdentityFields(kv)) {
    return hasSellerSideFields(kv);
  }
  return true;
}

function pickBuyerName(kv) {
  const exactKeys = [
    '购买方名称', '购方名称', '买方名称', '购买单位名称',
    '购货方名称', '购货单位名称',
    'BuyerName', 'buyer_name'
  ];
  for (const key of exactKeys) {
    const val = kv?.[key];
    if (val != null && String(val).trim() && !isPartySectionLabel(val)) {
      return String(val).trim();
    }
  }
  for (const [k, v] of Object.entries(kv || {})) {
    if (v == null) continue;
    if (/购买方.*名称|购方.*名称|买方.*名称|购货方.*名称|购货单位.*名称/i.test(k)) {
      const text = String(v).trim();
      if (text && !isPartySectionLabel(text)) return text;
    }
  }

  const blockName = pickPartyBlockName(kv, 'buyer');
  if (blockName) return blockName;

  if (hasPartyInfoBlockKeys(kv)) {
    if (hasDegradedPartyInfoBlocks(kv)) return '';
    const sellerBlockName = pickPartyBlockName(kv, 'seller');
    const genericName = kv?.名称;
    if (genericName != null && String(genericName).trim()) {
      const text = String(genericName).trim();
      if (!isPartySectionLabel(text)
        && (!sellerBlockName
          || normalizeSupplierCompanyName(text) !== normalizeSupplierCompanyName(sellerBlockName))) {
        return text;
      }
    }
  }
  return '';
}

function pickBuyerTaxNo(kv) {
  const exactKeys = [
    '购买方纳税人识别号',
    '购买方统一社会信用代码/纳税人识别号',
    '购买方统一社会信用代码',
    '购买方税号',
    '购方税号',
    '购方纳税人识别号',
    'BuyerTaxNo',
    'buyer_tax_no'
  ];
  for (const key of exactKeys) {
    const val = kv?.[key];
    if (val != null && String(val).trim()) return String(val).trim();
  }
  for (const [k, v] of Object.entries(kv || {})) {
    if (v == null) continue;
    const keyText = String(k);
    if (!/购|买|Buyer|buyer/i.test(keyText)) continue;
    if (/纳税人识别号|统一社会信用代码|TaxNo|tax_no/i.test(keyText)) {
      const text = String(v).trim();
      if (text) return text;
    }
  }

  const blockTaxNo = pickPartyBlockTaxNo(kv, 'buyer');
  if (blockTaxNo) return blockTaxNo;
  return '';
}

function pickSellerName(kv) {
  const explicitSellerKeys = [
    '销售方名称', '销方名称',
    '填开单位', '开票单位', '开票方', '承运人',
    'SellerName', 'seller_name', 'sellerName'
  ];
  for (const key of explicitSellerKeys) {
    const val = kv?.[key];
    if (val != null && String(val).trim()) return String(val).trim();
  }
  for (const [k, v] of Object.entries(kv || {})) {
    if (v == null || BUYER_KEY.test(k) || SELLER_META_KEY.test(k)) continue;
    if (/销售方.*名称|销方.*名称|填开单位|开票单位|承运人/i.test(k)) return String(v).trim();
  }

  const blockName = pickPartyBlockName(kv, 'seller');
  if (blockName) return blockName;

  const genericName = kv?.名称;
  if (genericName != null && String(genericName).trim()) {
    const text = String(genericName).trim();
    const buyerName = pickBuyerName(kv);
    if (!buyerName || normalizeSupplierCompanyName(text) !== normalizeSupplierCompanyName(buyerName)) {
      return text;
    }
  }
  return '';
}

function pickGenericSellerTaxNo(kv) {
  const genericTax = kv?.['统一社会信用代码/纳税人识别号'];
  if (genericTax != null && String(genericTax).trim()) {
    return String(genericTax).trim();
  }
  for (const [k, v] of Object.entries(kv || {})) {
    if (v == null || BUYER_KEY.test(k)) continue;
    const keyText = String(k);
    if (/纳税人识别号|统一社会信用代码|TaxNo|tax_no/i.test(keyText)) {
      const text = String(v).trim();
      if (text) return text;
    }
  }
  return '';
}

function pickSellerTaxNo(kv) {
  const explicitSellerTaxKeys = [
    '销售方统一社会信用代码/纳税人识别号',
    '销售方纳税人识别号',
    '销售方税号',
    '销售方统一社会信用代码',
    '销方税号',
    'SellerTaxNo',
    'seller_tax_no',
    'sellerTaxNo'
  ];
  for (const key of explicitSellerTaxKeys) {
    const val = kv?.[key];
    if (val != null && String(val).trim()) return String(val).trim();
  }

  let sellerTaxNo = pickPartyBlockTaxNo(kv, 'seller');
  if (!sellerTaxNo && (shouldUseGenericSellerFields(kv) || hasPartyInfoBlockKeys(kv))) {
    sellerTaxNo = pickGenericSellerTaxNo(kv);
  }

  const buyerTaxNo = pickBuyerTaxNo(kv);
  if (sellerTaxNo && buyerTaxNo
    && normalizeSupplierTaxNo(sellerTaxNo) === normalizeSupplierTaxNo(buyerTaxNo)) {
    return '';
  }
  return sellerTaxNo;
}

function parseInvoiceOcrKv(ocrRaw) {
  if (!ocrRaw) return null;
  if (typeof ocrRaw === 'string') {
    try {
      return JSON.parse(ocrRaw);
    } catch {
      return null;
    }
  }
  return ocrRaw;
}

export function normalizeSupplierCompanyName(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[()（）]/g, '')
    .toLowerCase();
}

export function normalizeSupplierTaxNo(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
}

export function extractSellerInfoFromInvoiceOcrRaw(ocrRaw) {
  const kv = parseInvoiceOcrKv(ocrRaw);
  if (!kv) return { seller_name: null, seller_tax_no: null };
  return {
    seller_name: pickSellerName(kv) || null,
    seller_tax_no: pickSellerTaxNo(kv) || null
  };
}

export function extractBuyerInfoFromInvoiceOcrRaw(ocrRaw) {
  const kv = parseInvoiceOcrKv(ocrRaw);
  if (!kv) return { buyer_name: null, buyer_tax_no: null };
  return {
    buyer_name: pickBuyerName(kv) || null,
    buyer_tax_no: pickBuyerTaxNo(kv) || null
  };
}

export function isTravelItineraryOcrRaw(ocrRaw) {
  const kv = parseInvoiceOcrKv(ocrRaw);
  return Boolean(kv && isTravelItineraryLike(kv));
}

export function extractSellerInfoFromClaimLine(line) {
  const fromOcr = extractSellerInfoFromInvoiceOcrRaw(line?.ocr_raw);
  const verifyDetail = line?.vat_verify?.detail;
  return {
    seller_name: fromOcr.seller_name || verifyDetail?.saler_name || null,
    seller_tax_no: fromOcr.seller_tax_no || null
  };
}

export function extractBuyerInfoFromClaimLine(line) {
  const fromOcr = extractBuyerInfoFromInvoiceOcrRaw(line?.ocr_raw);
  const verifyDetail = line?.vat_verify?.detail;
  return {
    buyer_name: fromOcr.buyer_name || verifyDetail?.purchaser_name || null,
    buyer_tax_no: fromOcr.buyer_tax_no || null
  };
}

export function lineHasUploadedInvoice(line) {
  if (!line || typeof line !== 'object') return false;
  if (Array.isArray(line.invoice_files) && line.invoice_files.length > 0) return true;
  if (line.ocr_raw) return true;
  if (String(line.invoice_no || '').trim()) return true;
  return false;
}

export function buildExpenseCompanyNameCandidates({
  expenseCompanyId,
  expenseCompanyOptions,
  expenseCompanyNames
} = {}) {
  const candidates = new Set();
  const add = (value) => {
    const normalized = normalizeSupplierCompanyName(value);
    if (normalized) candidates.add(normalized);
  };
  (expenseCompanyNames || []).forEach(add);
  const option = (expenseCompanyOptions || []).find(
    (item) => String(item.id) === String(expenseCompanyId)
  );
  if (option) {
    add(option.label);
    add(option.short_name);
    add(option.company_name);
  }
  return candidates;
}

export function collectExpenseCompanyNamesFromOption(option) {
  if (!option) return [];
  return [...new Set([option.label, option.short_name, option.company_name].filter(Boolean))];
}

export function extractInvoiceNoFromOcrRaw(ocrRaw) {
  const kv = parseInvoiceOcrKv(ocrRaw);
  if (!kv) return null;
  const no = pickField(kv, [
    '发票号码', 'InvoiceNumber', 'invoiceNumber', 'invoice_no', 'invoiceNo',
    '票据号码', '机打发票号码', '电子客票号码'
  ]);
  return no || null;
}

export function formatInvoiceBuyerDisplayText({ ocrRaw, verify } = {}) {
  const fromOcr = extractBuyerInfoFromInvoiceOcrRaw(ocrRaw);
  const buyerName = fromOcr.buyer_name || verify?.detail?.purchaser_name || null;
  return buyerName ? String(buyerName).trim() : '';
}

export function formatInvoiceIssuerDisplayText({ ocrRaw, invoiceNo, verify } = {}) {
  const kv = parseInvoiceOcrKv(ocrRaw);
  const fromOcr = extractSellerInfoFromInvoiceOcrRaw(ocrRaw);
  const sellerName = fromOcr.seller_name || verify?.detail?.saler_name || null;
  const sellerTaxNo = fromOcr.seller_tax_no || null;
  const resolvedInvoiceNo = String(invoiceNo || extractInvoiceNoFromOcrRaw(ocrRaw) || '').trim();
  if (!sellerName && !sellerTaxNo && !resolvedInvoiceNo) return '';

  const travelLike = Boolean(kv && isTravelItineraryLike(kv));
  const parts = [];
  if (sellerName) {
    parts.push(travelLike && !sellerTaxNo ? `开票单位 ${sellerName}` : sellerName);
  }
  if (sellerTaxNo) parts.push(`税号 ${sellerTaxNo}`);
  if (resolvedInvoiceNo) parts.push(`发票号 ${resolvedInvoiceNo}`);
  return parts.join(' · ');
}

export function assertExpenseCompanyMatchesInvoices({
  expenseCompanyId,
  expenseCompanyOptions,
  expenseCompanyNames,
  lines
}) {
  const candidates = buildExpenseCompanyNameCandidates({
    expenseCompanyId,
    expenseCompanyOptions,
    expenseCompanyNames
  });
  const option = (expenseCompanyOptions || []).find(
    (item) => String(item.id) === String(expenseCompanyId)
  );
  const displayName = option?.label || option?.short_name || option?.company_name || '费用承担公司';

  if (!expenseCompanyId || !candidates.size) {
    return '请选择费用承担公司';
  }

  for (let index = 0; index < (lines || []).length; index += 1) {
    const line = lines[index];
    const invoiceType = String(line?.invoice_type || '').trim();
    if (invoiceType === 'other') continue;
    if (!lineHasUploadedInvoice(line)) continue;

    const lineNo = index + 1;
    const { buyer_name: buyerName } = extractBuyerInfoFromClaimLine(line);
    if (!buyerName) {
      return `第 ${lineNo} 行发票未能识别购买方/购货方名称，无法校验费用承担公司，请重新上传发票`;
    }

    if (!candidates.has(normalizeSupplierCompanyName(buyerName))) {
      return `第 ${lineNo} 行发票购买方「${buyerName}」与费用承担公司「${displayName}」不一致`;
    }
  }

  return null;
}

export function assertSupplierPayeeMatchesInvoices({ payeeType, payeeName, taxNo, lines }) {
  if (payeeType !== 'supplier') return null;

  const normalizedPayeeName = normalizeSupplierCompanyName(payeeName);
  const normalizedTaxNo = normalizeSupplierTaxNo(taxNo);
  if (!normalizedPayeeName) return '供应商须填写收款方名称';
  if (!normalizedTaxNo) return '供应商须填写对方税号';

  for (let index = 0; index < (lines || []).length; index += 1) {
    const line = lines[index];
    const invoiceType = String(line?.invoice_type || '').trim();
    if (invoiceType !== 'special' && invoiceType !== 'general') continue;

    const lineNo = index + 1;
    const { seller_name: sellerName, seller_tax_no: sellerTaxNo } = extractSellerInfoFromClaimLine(line);

    if (!sellerName || !sellerTaxNo) {
      if (isTravelItineraryOcrRaw(line?.ocr_raw)) continue;
      return `第 ${lineNo} 行发票未能识别销售方名称或税号，无法校验供应商信息，请重新上传发票`;
    }

    if (normalizeSupplierCompanyName(sellerName) !== normalizedPayeeName) {
      return `第 ${lineNo} 行发票销售方「${sellerName}」与供应商收款方名称「${payeeName}」不一致`;
    }

    if (normalizeSupplierTaxNo(sellerTaxNo) !== normalizedTaxNo) {
      return `第 ${lineNo} 行发票销售方税号「${sellerTaxNo}」与供应商税号「${taxNo}」不一致`;
    }
  }

  return null;
}

// pickField 保留供测试/扩展
export { pickField, pickBuyerName, pickBuyerTaxNo, isTravelItineraryLike };
